<template>
  <!-- OTP Modal -->
  <div class="modal fade otp-modal" tabindex="-1" role="dialog" aria-labelledby="myLargeModalLabel" aria-hidden="true">
    <div class="modal-dialog w-100 modal-md modal-dialog-centered modal-full">
      <div class="modal-content w-100 bg-light-blue border-info">
        <div class="modal-header text-black text-uppercase border-0">
          <h4 class="modal-title">Invoice Generation Confirmation</h4>
          <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
        </div>
        <div class="modal-body">
          <!-- STEP 1: Mode Selection (when no selection made and not in success state) -->
          <div v-if="!invoicesSentSuccess && selectedCount === 0 && !showFiscalYearSelection && !showOTPEntry" class="mode-selection">
            <div class="alert alert-info border-info border-2" role="alert">
              <h5 class="alert-heading text-black text-uppercase">
                <i class="bx bx-info-circle me-2"></i>No Records Selected
              </h5>
              <p>You haven't selected any specific records. Would you like to send invoices to <strong>ALL pending records</strong>?</p>
              <hr class="border-info">
              <div class="d-flex flex-column gap-2">
                <div class="card bg-light border-0">
                  <div class="card-body">
                    <div class="d-flex justify-content-between align-items-center">
                      <div>
                        <h6 class="mb-1"><i class="bx bx-select-multiple text-primary me-2"></i>Total Pending Records</h6>
                        <small class="text-muted">Records that haven't been invoiced yet</small>
                      </div>
                      <h3 class="text-primary mb-0">{{ numberFormat(totalPendingRecords) }}</h3>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="alert alert-warning border-warning border-2 mb-4" role="alert">
              <h6 class="alert-heading mb-2">
                <i class="bx bx-shield-alt-2 me-2"></i>Batch Processing Information
              </h6>
              <ul class="mb-0 ps-3">
                <li>Records will be processed in batches of <strong>{{ batchSize }}</strong></li>
                <li>Only <strong>PENDING</strong> records will be processed (already invoiced records will be skipped)</li>
                <li>You can <strong>pause and resume</strong> at any time</li>
                <li>A progress tracker will show real-time status</li>
                <li>Failed records can be <strong>retried</strong> after completion</li>
              </ul>
            </div>

            <div class="d-grid gap-3">
              <button @click="initiateSendAll" class="btn btn-success btn-lg text-uppercase fw-bold">
                <i class="bx bx-send me-2"></i>
                Send All Pending Invoices ({{ numberFormat(totalPendingRecords) }})
              </button>
              <button type="button" data-bs-dismiss="modal" class="btn btn-outline-secondary text-uppercase">
                Cancel - Select Records Manually
              </button>
            </div>
          </div>

          <!-- STEP 2: Fiscal Year Selection (NEW STEP - Shows before OTP) -->
          <div v-if="!invoicesSentSuccess && (selectedCount > 0 || showFiscalYearSelection) && !showOTPEntry" class="fiscal-year-selection">
            <div class="alert alert-primary border-primary border-2" role="alert">
              <h4 class="alert-heading text-black text-uppercase">
                <i class="bx bx-calendar me-2"></i>Select Fiscal Year
              </h4>
              <p>Please select the fiscal year for which these invoices will be generated. This is a <strong>mandatory</strong> selection.</p>
              <hr class="border-primary">

              <!-- Fiscal Year Selection Info -->
              <div class="mb-4">
                <div v-if="isSendAllMode" class="mb-3">
                  <p class="mb-0"><strong>Batch Processing Mode</strong></p>
                  <p class="mb-2">
                    <span class="badge bg-primary fs-6 me-2">{{ numberFormat(totalPendingRecords) }} records</span>
                    will be invoiced for the selected fiscal year
                  </p>
                </div>
                <div v-else>
                  <p class="mb-0"><strong>Selected Records</strong></p>
                  <p class="mb-2">
                    <span class="badge bg-primary fs-6 me-2">{{ numberFormat(selectedCount) }} invoices</span>
                    will be generated for the selected fiscal year
                  </p>
                </div>
              </div>

              <!-- Fiscal Year Dropdown -->
              <div class="mb-3">
                <label for="fiscalYearSelect" class="form-label fw-bold">
                  Fiscal Year <span class="text-danger">*</span>
                </label>
                <select
                    id="fiscalYearSelect"
                    v-model="selectedFiscalYear"
                    class="form-select form-select-lg border-primary"
                    :class="{'is-invalid': fiscalYearError}"
                >
                  <option value="" disabled>-- Select Fiscal Year --</option>
                  <option v-for="year in availableFiscalYears" :key="year" :value="year">
                    {{ year }}
                  </option>
                </select>
                <div v-if="fiscalYearError" class="invalid-feedback">
                  Please select a fiscal year before proceeding.
                </div>
                <small class="text-muted d-block mt-2">
                  <i class="bx bx-info-circle me-1"></i>
                  Default: {{ defaultFiscalYear }} ({{ fiscalYearNote }})
                </small>
              </div>

              <!-- Permit Validity Period Display -->
              <div v-if="selectedFiscalYear" class="permit-validity-section mb-4">
                <!-- Selected Fiscal Year Card -->
                <div class="card bg-success bg-opacity-10 border-success mb-3">
                  <div class="card-body py-3">
                    <div class="d-flex align-items-center">
                      <i class="bx bx-check-circle text-success fs-3 me-3"></i>
                      <div>
                        <h6 class="mb-0 text-success">Fiscal Year Selected</h6>
                        <span class="fs-4 fw-bold text-success">{{ selectedFiscalYear }}</span>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Permit Validity Period Card -->
                <div class="card border-info mb-3">
                  <div class="card-header bg-info text-white">
                    <h6 class="mb-0">
                      <i class="bx bx-calendar-check me-2"></i>
                      Permit Validity Period
                    </h6>
                  </div>
                  <div class="card-body">
                    <div class="row text-center">
                      <div class="col-5">
                        <div class="border rounded p-3 bg-light">
                          <small class="text-muted d-block mb-1">STARTS FROM</small>
                          <i class="bx bx-calendar-event text-success fs-4"></i>
                          <h5 class="mb-0 text-success fw-bold">{{ permitStartDate }}</h5>
                          <small class="text-muted">{{ permitStartDay }}</small>
                        </div>
                      </div>
                      <div class="col-2 d-flex align-items-center justify-content-center">
                        <i class="bx bx-right-arrow-alt text-muted fs-2"></i>
                      </div>
                      <div class="col-5">
                        <div class="border rounded p-3 bg-light">
                          <small class="text-muted d-block mb-1">EXPIRES ON</small>
                          <i class="bx bx-calendar-x text-danger fs-4"></i>
                          <h5 class="mb-0 text-danger fw-bold">{{ permitEndDate }}</h5>
                          <small class="text-muted">{{ permitEndDay }}</small>
                        </div>
                      </div>
                    </div>
                    <div class="text-center mt-3">
                      <span class="badge bg-primary fs-6">
                        <i class="bx bx-time me-1"></i>
                        Validity: 12 Months (1 Year)
                      </span>
                    </div>
                  </div>
                </div>

                <!-- Important Notice -->
                <div class="alert alert-warning border-warning mb-3">
                  <h6 class="alert-heading mb-2">
                    <i class="bx bx-error-circle me-2"></i>Important Notice
                  </h6>
                  <ul class="mb-0 small">
                    <li>All permits generated will be valid from <strong>{{ permitStartDate }}</strong> to <strong>{{ permitEndDate }}</strong></li>
                    <li>Business owners will receive invoices for the <strong>{{ selectedFiscalYear }}</strong> fiscal year</li>
                    <li>This action <strong>cannot be undone</strong> once invoices are sent</li>
                  </ul>
                </div>

                <!-- Confirmation Checkbox -->
                <div class="card border-dark mb-3">
                  <div class="card-body">
                    <div class="form-check">
                      <input
                          class="form-check-input border-dark"
                          type="checkbox"
                          id="confirmFiscalYear"
                          v-model="fiscalYearConfirmed"
                          style="width: 1.5em; height: 1.5em;"
                      >
                      <label class="form-check-label ms-2 fw-bold" for="confirmFiscalYear">
                        I confirm that I want to generate invoices for <span class="text-primary">Fiscal Year {{ selectedFiscalYear }}</span>
                      </label>
                    </div>
                    <div class="form-check mt-2">
                      <input
                          class="form-check-input border-dark"
                          type="checkbox"
                          id="confirmValidityPeriod"
                          v-model="validityPeriodConfirmed"
                          style="width: 1.5em; height: 1.5em;"
                      >
                      <label class="form-check-label ms-2 fw-bold" for="confirmValidityPeriod">
                        I understand that permits will be valid from <span class="text-success">{{ permitStartDate }}</span> to <span class="text-danger">{{ permitEndDate }}</span>
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div v-if="message != null" class="alert alert-danger" role="alert">
              {{ message }}
            </div>

            <!-- Confirmation Error -->
            <div v-if="confirmationError" class="alert alert-danger" role="alert">
              <i class="bx bx-error-circle me-2"></i>
              Please check both confirmation boxes before proceeding.
            </div>

            <div class="d-grid gap-2">
              <button
                  @click="proceedToOTP"
                  class="btn btn-success btn-lg fw-bold text-uppercase"
                  :disabled="!selectedFiscalYear || !canProceed"
                  :class="{'btn-secondary': !canProceed}"
              >
                <i class="bx bx-right-arrow-alt me-2"></i>
                Proceed to Verification
              </button>
              <button @click="goBackFromFiscalYear" type="button" class="btn btn-outline-secondary text-uppercase">
                <i class="bx bx-left-arrow-alt me-2"></i>
                Go Back
              </button>
            </div>
          </div>

          <!-- STEP 3: OTP Entry Section -->
          <div v-if="!invoicesSentSuccess && showOTPEntry" class="otp-entry">
            <div class="alert alert-info border-info border-2" role="alert">
              <h4 class="alert-heading text-black text-uppercase">OTP Verification</h4>

              <p>An OTP has been sent to your phone: <strong>{{ maskPhoneNumber(sessionPhoneNumber) }}</strong>. Enter the OTP to confirm the generation of invoices. Selected business owners will receive their respective invoice notifications via both email and SMS.</p>

              <hr class="text-black border-2 border-black border-info text-info">

              <!-- Fiscal Year & Validity Summary Badge -->
              <div class="mb-3">
                <div class="card bg-primary text-white mb-2">
                  <div class="card-body py-2">
                    <div class="row align-items-center">
                      <div class="col-4 text-center border-end">
                        <small class="d-block opacity-75">Fiscal Year</small>
                        <span class="fs-5 fw-bold">{{ selectedFiscalYear }}</span>
                      </div>
                      <div class="col-4 text-center border-end">
                        <small class="d-block opacity-75">Starts</small>
                        <span class="fw-bold">{{ permitStartDateShort }}</span>
                      </div>
                      <div class="col-4 text-center">
                        <small class="d-block opacity-75">Expires</small>
                        <span class="fw-bold">{{ permitEndDateShort }}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Show different info based on mode -->
              <div v-if="isSendAllMode" class="mb-3">
                <p class="mb-0"><strong>Batch Processing Mode</strong></p>
                <p class="mb-2">
                  <span class="badge bg-primary fs-6 me-2">{{ numberFormat(totalPendingRecords) }} records</span>
                  will be processed in batches of {{ batchSize }}
                </p>
                <small class="text-muted">Only PENDING records will be processed. Already invoiced records will be skipped.</small>
              </div>
              <div v-else>
                <p class="mb-0"><strong>Selected Invoices for Generation</strong></p>
                <p><strong>{{ numberFormat(selectedCount) }} invoices</strong> out of <strong>{{ numberFormat(totalEntries) }}</strong> have been selected.</p>
              </div>

              <div class="hstack gap-3">
                <input v-model="localOtpCodes" class="form-control me-auto border-black" type="text" placeholder="Enter OTP" aria-label="Enter OTP" spellcheck="false" data-ms-editor="true">

                <button @click="onResendOTP()" type="button" class="btn btn-warning text-dark fw-bold text-nowrap text-uppercase">
                  <div class="d-flex align-items-center">
                    <span>Resend OTP</span>
                    <div v-if="loadingOTP" class="spinner-border spinner-border-sm text-dark ms-2" role="status">
                      <span class="sr-only">Loading...</span>
                    </div>
                  </div>
                </button>
              </div>
            </div>

            <div v-if="message != null" class="alert alert-danger" role="alert">
              {{ message }}
            </div>

            <button @click="onVerifyOTP()" class="btn btn-success fw-bold mt-4 text-uppercase w-100 d-flex justify-content-center align-items-center text-center" title="Confirm and Process">
              <div>{{ isSendAllMode ? 'Start Batch Processing' : 'Confirm' }}</div>
              <div v-if="loading" class="spinner-border text-white ms-2" style="height: 19px; width: 19px;" role="status">
                <span class="sr-only">Loading...</span>
              </div>
            </button>

            <button @click="goBackToFiscalYear" type="button" class="btn btn-outline-secondary mt-2 w-100 text-uppercase">
              <i class="bx bx-left-arrow-alt me-2"></i>
              Change Fiscal Year
            </button>
          </div>

          <!-- Success Section -->
          <div v-if="invoicesSentSuccess" class="p-4 pb-1 archive-done px-1 d-flex align-items-center justify-content-center flex-column pt-1">
            <div class="flex-shrink-0 align-self-center mb-4">
              <div class="avatar-md rounded-circle bg-primary mini-stat-icon" title="Action Successful">
                <span class="avatar-title rounded-circle bg-success">
                    <i class="bx bx-check-circle fs-1" title="Invoices Generated"></i>
                </span>
              </div>
            </div>
            <h4 class="text-uppercase text-dark modal-title fw-bold" title="Invoices Successfully Sent">Invoices Generated</h4>
            <p class="text-center px-5" title="Invoice Generation Confirmation">
              A total of <strong>{{ numberFormat(selectedCount) }} invoices</strong> have been successfully generated for <strong>Fiscal Year {{ selectedFiscalYear }}</strong> and sent to the selected business owners via <strong>SMS</strong> and <strong>Email</strong>.
            </p>

            <!-- Validity Period Summary -->
            <div class="card border-success mb-3 w-100">
              <div class="card-body py-2 text-center">
                <small class="text-muted">Permit Validity Period</small>
                <div>
                  <span class="text-success fw-bold">{{ permitStartDate }}</span>
                  <span class="mx-2">to</span>
                  <span class="text-danger fw-bold">{{ permitEndDate }}</span>
                </div>
              </div>
            </div>

            <div class="w-100 mt-3 d-flex justify-content-between">
              <button type="button" data-bs-dismiss="modal" aria-label="Close" class="btn btn-outline-secondary waves-effect w-45 btn-close-btn-otp text-uppercase fw-bold">
                Close
              </button>
              <a @click="onGoToNewRegister()" href="javascript: void(0);" class="btn btn-primary waves-effect w-45 text-uppercase fw-bold">
                View Sent Invoices
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */

export default {
  name: "OTPModal",
  props: {
    selectedCount: {
      type: Number,
      default: 0
    },
    totalEntries: {
      type: Number,
      default: 0
    },
    totalPendingRecords: {
      type: Number,
      default: 0
    },
    sessionPhoneNumber: {
      type: String,
      default: ''
    },
    invoicesSentSuccess: {
      type: Boolean,
      default: false
    },
    loading: {
      type: Boolean,
      default: false
    },
    loadingOTP: {
      type: Boolean,
      default: false
    },
    message: {
      type: String,
      default: null
    },
    otpCodes: {
      type: String,
      default: ''
    },
    batchSize: {
      type: Number,
      default: 10
    },
    // New prop to receive fiscal year from parent if needed
    fiscalYear: {
      type: [String, Number],
      default: null
    }
  },
  data() {
    return {
      localOtpCodes: this.otpCodes,
      isUpdatingFromProp: false,
      showOTPEntry: false,
      showFiscalYearSelection: false,
      isSendAllMode: false,
      selectedFiscalYear: '',
      fiscalYearError: false,
      confirmationError: false,
      // Confirmation checkboxes
      fiscalYearConfirmed: false,
      validityPeriodConfirmed: false
    }
  },
  computed: {
    // Get current year
    currentYear() {
      return new Date().getFullYear();
    },
    // Get current month (0-indexed, so October = 9, November = 10, December = 11)
    currentMonth() {
      return new Date().getMonth();
    },
    // Determine default fiscal year based on current date
    // If in last 3 months (Oct, Nov, Dec), default to next year
    // Otherwise, default to current year
    defaultFiscalYear() {
      const month = this.currentMonth;
      // October = 9, November = 10, December = 11
      if (month >= 9) {
        return this.currentYear + 1;
      }
      return this.currentYear;
    },
    // Note explaining why the default was chosen
    fiscalYearNote() {
      const month = this.currentMonth;
      if (month >= 9) {
        return 'Auto-selected next year since we are in the last quarter';
      }
      return 'Auto-selected current year';
    },
    // Available fiscal years for selection (current year - 1, current year, next year, next year + 1)
    availableFiscalYears() {
      const currentYear = this.currentYear;
      return [
        currentYear - 1,
        currentYear,
        currentYear + 1,
        currentYear + 2
      ];
    },
    // Permit Start Date - 1st January of selected fiscal year
    permitStartDate() {
      if (!this.selectedFiscalYear) return '';
      return `1st January ${this.selectedFiscalYear}`;
    },
    // Permit Start Date Short format
    permitStartDateShort() {
      if (!this.selectedFiscalYear) return '';
      return `01/01/${this.selectedFiscalYear}`;
    },
    // Permit Start Day of week
    permitStartDay() {
      if (!this.selectedFiscalYear) return '';
      const date = new Date(this.selectedFiscalYear, 0, 1); // January 1st
      const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
      return days[date.getDay()];
    },
    // Permit End Date - 31st December of selected fiscal year
    permitEndDate() {
      if (!this.selectedFiscalYear) return '';
      return `31st December ${this.selectedFiscalYear}`;
    },
    // Permit End Date Short format
    permitEndDateShort() {
      if (!this.selectedFiscalYear) return '';
      return `31/12/${this.selectedFiscalYear}`;
    },
    // Permit End Day of week
    permitEndDay() {
      if (!this.selectedFiscalYear) return '';
      const date = new Date(this.selectedFiscalYear, 11, 31); // December 31st
      const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
      return days[date.getDay()];
    },
    // Check if user can proceed (both confirmations checked)
    canProceed() {
      return this.selectedFiscalYear && this.fiscalYearConfirmed && this.validityPeriodConfirmed;
    }
  },
  watch: {
    otpCodes(newVal) {
      this.isUpdatingFromProp = true;
      this.localOtpCodes = newVal;
      this.$nextTick(() => { this.isUpdatingFromProp = false; });
    },
    localOtpCodes(newVal) {
      if (!this.isUpdatingFromProp) {
        this.$emit('update:otpCodes', newVal);
      }
    },
    // Reset state when modal opens with selections
    selectedCount(newVal) {
      if (newVal > 0) {
        this.showOTPEntry = false;
        this.showFiscalYearSelection = true; // Show fiscal year selection first
        this.isSendAllMode = false;
        // Set default fiscal year
        if (!this.selectedFiscalYear) {
          this.selectedFiscalYear = this.defaultFiscalYear;
        }
      }
    },
    // Watch for fiscal year prop changes
    fiscalYear(newVal) {
      if (newVal) {
        this.selectedFiscalYear = newVal;
      }
    },
    // Emit fiscal year changes to parent
    selectedFiscalYear(newVal) {
      this.$emit('update:fiscalYear', newVal);
      // Clear errors when a selection is made
      if (newVal) {
        this.fiscalYearError = false;
      }
      // Reset confirmations when fiscal year changes
      this.fiscalYearConfirmed = false;
      this.validityPeriodConfirmed = false;
      this.confirmationError = false;
    },
    // Clear confirmation error when checkboxes are checked
    fiscalYearConfirmed(newVal) {
      if (newVal && this.validityPeriodConfirmed) {
        this.confirmationError = false;
      }
    },
    validityPeriodConfirmed(newVal) {
      if (newVal && this.fiscalYearConfirmed) {
        this.confirmationError = false;
      }
    }
  },
  mounted() {
    // Set default fiscal year on mount
    this.selectedFiscalYear = this.defaultFiscalYear;
  },
  methods: {
    numberFormat(num) {
      const britishNumberFormatter = new Intl.NumberFormat("en-GB");
      return britishNumberFormatter.format(num);
    },
    maskPhoneNumber(input) {
      if (!input || typeof input !== 'string') {
        return input;
      }

      const digits = input.replace(/\D/g, '');

      if (digits.length > 4) {
        return input.slice(0, 2) + input.slice(2, -2).replace(/\d/g, '*') + input.slice(-2);
      }

      return input;
    },
    // Initiate Send All - goes to fiscal year selection first
    initiateSendAll() {
      this.isSendAllMode = true;
      this.showFiscalYearSelection = true;
      // Set default fiscal year
      if (!this.selectedFiscalYear) {
        this.selectedFiscalYear = this.defaultFiscalYear;
      }
      // Reset confirmations
      this.fiscalYearConfirmed = false;
      this.validityPeriodConfirmed = false;
    },
    // Proceed from fiscal year selection to OTP verification
    proceedToOTP() {
      // Validate fiscal year selection
      if (!this.selectedFiscalYear) {
        this.fiscalYearError = true;
        return;
      }

      // Validate confirmations
      if (!this.fiscalYearConfirmed || !this.validityPeriodConfirmed) {
        this.confirmationError = true;
        return;
      }

      this.fiscalYearError = false;
      this.confirmationError = false;
      this.showFiscalYearSelection = false;
      this.showOTPEntry = true;

      // Emit the selected fiscal year to parent
      this.$emit('update:fiscalYear', this.selectedFiscalYear);

      // Send OTP
      this.$emit('sendOTPMessage');
    },
    // Go back from OTP to fiscal year selection
    goBackToFiscalYear() {
      this.showOTPEntry = false;
      this.showFiscalYearSelection = true;
    },
    // Go back from fiscal year selection
    goBackFromFiscalYear() {
      this.showFiscalYearSelection = false;
      // Reset confirmations
      this.fiscalYearConfirmed = false;
      this.validityPeriodConfirmed = false;
      if (this.isSendAllMode) {
        this.isSendAllMode = false;
      }
    },
    cancelSendAll() {
      this.isSendAllMode = false;
      this.showOTPEntry = false;
      this.showFiscalYearSelection = false;
      // Reset confirmations
      this.fiscalYearConfirmed = false;
      this.validityPeriodConfirmed = false;
    },
    onResendOTP() {
      this.$emit('sendOTPMessage');
    },
    onVerifyOTP() {
      // Validate fiscal year one more time
      if (!this.selectedFiscalYear) {
        this.fiscalYearError = true;
        this.showOTPEntry = false;
        this.showFiscalYearSelection = true;
        return;
      }

      if (this.isSendAllMode) {
        this.$emit('verifySendAllOTP', this.selectedFiscalYear);
      } else {
        this.$emit('verifyOTP', this.selectedFiscalYear);
      }
    },
    onGoToNewRegister() {
      this.$emit('goToNewRegister');
    },
    // Reset modal state (called from parent when modal closes)
    resetState() {
      this.showOTPEntry = false;
      this.showFiscalYearSelection = false;
      this.isSendAllMode = false;
      this.localOtpCodes = '';
      this.fiscalYearError = false;
      this.confirmationError = false;
      // Reset confirmations
      this.fiscalYearConfirmed = false;
      this.validityPeriodConfirmed = false;
      // Reset to default fiscal year
      this.selectedFiscalYear = this.defaultFiscalYear;
    },
    // Get the selected fiscal year (can be called from parent)
    getSelectedFiscalYear() {
      return this.selectedFiscalYear;
    }
  }
}
</script>

<style scoped>
.w-45 {
  width: 45%;
}

.form-check-input:checked {
  background-color: #198754;
  border-color: #198754;
}

.permit-validity-section .card {
  transition: all 0.3s ease;
}

.permit-validity-section .card:hover {
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
}
</style>